import { Injectable, Signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { mapToVoid } from '@examdojo/rxjs';
import isEqual from 'lodash/isEqual';
import { BehaviorSubject, distinctUntilChanged, isObservable, merge, Observable, scan, tap } from 'rxjs';

type Context = Record<string, unknown>;

@Injectable({
  providedIn: 'root',
})
export class DebuggingContextService {
  constructor(private readonly errorHandlerService: ErrorHandlerService) {
    merge(this.attachContextToErrorHandler(), this.logContextHistory()).pipe(takeUntilDestroyed()).subscribe();
  }
  private readonly context$$ = new BehaviorSubject<Context>({});
  readonly context$ = this.context$$.asObservable();

  private readonly contextHistory$$ = new BehaviorSubject<Array<{ timestamp: Date; context: Context }>>([]);
  readonly contextHistory$ = this.contextHistory$$.asObservable();

  setContext(key: string, value: unknown) {
    this.context$$.next({ ...this.context$$.value, [key]: value });
  }

  getContext() {
    return this.context$$.value;
  }

  resetContextKey(key: string) {
    const { [key]: _, ...rest } = this.context$$.value;
    this.context$$.next(rest);
  }

  syncDebuggingContext(key: string, source: Observable<unknown> | Signal<unknown>) {
    const source$ = isObservable(source) ? source : toObservable(source);

    return source$.pipe(
      tap({
        next: (activity) => {
          this.setContext(key, activity);
        },
        complete: () => {
          this.resetContextKey(key);
        },
      }),
      mapToVoid(),
    );
  }

  log(key: string, data: unknown) {
    const existingValue = this.context$$.value[key];
    const existingData = Array.isArray(existingValue) ? existingValue : [];
    const newData = { timestamp: new Date(), data };
    this.setContext(key, [...existingData, newData]);
  }

  private attachContextToErrorHandler() {
    return this.context$.pipe(
      tap((context) => {
        this.errorHandlerService._setErrorContext(context);
      }),
    );
  }

  private logContextHistory() {
    return this.context$.pipe(
      distinctUntilChanged(isEqual),
      scan<Context, Array<{ timestamp: Date; context: Context }>>(
        (log, curr) => [...log, { timestamp: new Date(), context: curr }],
        [],
      ),
      tap((history) => this.contextHistory$$.next(history)),
    );
  }
}
